import { useDispatch, useSelector } from "react-redux";
import LoginModel from "../../components/Models/LoginModel";
import { RootState, AppDispatch } from "../../store";
import {
  updateField,
  setLoading,
  setErrors,
  setFieldError,
  resetLogin,
} from "./loginSlice";
import { handleInputChange } from "../../components/commonfunctions/handleInputChange";
import { formEmptyValidation } from "../../components/commonfunctions/formEmptyValidation";
import { validateInput } from "../../components/commonfunctions/validateInput";
import { signIn, confirmSignIn, fetchAuthSession } from "aws-amplify/auth";
// import { identifyOpenReplayUser } from "../../openreplay"; // OpenReplay disabled for now
import LoginForm from "../../components/Login/LoginForm";
import { useContext, useEffect, useState } from "react";
import { showToast } from "../../components/toastMessage/toastSlice";
import { resetOtp, setErrorMessage } from "./otpSlice";
import Otp from "./otp";
import { useNavigate } from "react-router";
import GlobalContext from "../../components/Context/GlobalContext";
import { contents } from "./AuthContents";
import useTranslation from "../../components/CustomHooks/useTranslation";
import { setPrivilegedArray } from "../../store/slices/privilegeSlice";
import { setUserDetails, setUserEmail } from "../../store/slices/userEmail";
import useApi from "../../services/useApi";
// import fetchFeatureFlags from "../../functions/appconfig";

export default function Login() {
  const dispatch = useDispatch<AppDispatch>();
  const [isSuccess, setIsSuccess] = useState(false);
  const Navigate = useNavigate();
  const { setIsLoggedIn } = useContext(GlobalContext);
  const { email_address, password, loading, errors, fieldError } = useSelector(
    (state: RootState) => state.login
  );
  const otp = useSelector((state: any) => state.otp.otp);
  const { translate, replace } = useTranslation(contents["login"]);
  const { getAPI } = useApi();
  const handleSubmit = async (id?: string) => {
    if (id !== "otp") {
      dispatch(setLoading(true));
    }
    // Step 1: Empty Field Validation
    const emptyFieldErrors = formEmptyValidation({ email_address, password });
    if (Object.keys(emptyFieldErrors).length > 0) {
      dispatch(setErrors(emptyFieldErrors));
      dispatch(setLoading(false));
      return;
    }

    // Step 2: Input-Specific Validation
    const inputValidationErrors = validateInput({ email_address, password });
    if (Object.keys(inputValidationErrors).length > 0) {
      dispatch(setErrors(inputValidationErrors));
      dispatch(setLoading(false));
      return;
    }

    // Step 3: Cognito Sign-In
    try {
      const user = await signIn({
        username: email_address,
        password: password,
        options: {
          authFlowType: "CUSTOM_WITH_SRP",
        },
      });

      if (id === "otp") {
        dispatch(
          showToast({
            type: "info",
            message: translate(`OTP resent`),
          })
        );
      } else {
        dispatch(
          showToast({
            type: "info",
            message: replace(`OTP has been sent to {{email_address}}`, {
              email_address,
            }),
          })
        );
      }
      dispatch(resetOtp());
      setIsSuccess(true);
      dispatch(setLoading(false));
      // Redirect to dashboard or perform other actions on successful login
    } catch (error: any) {
      console.error("Error during login:", error);
      if (error.message === "User is disabled.") {
        // dispatch(setFieldError("This user is deactivated"));
        dispatch(
          showToast({
            type: "error",
            message: translate("Your account is deactivated. Please contact support."),
          })
        );
      } else {
        dispatch(setFieldError("Invalid credentials"));
      }
      dispatch(setLoading(false));
    }
  };
  const handleDispatch = (
    field: "email_address" | "password",
    value: string
  ) => {
    dispatch(updateField({ field, value }));
  };

  const handleInputError = (
    field: "email_address" | "password",
    value: string
  ) => {
    dispatch(updateField({ field, value }));
  };
  const handleInputValue = (
    id: "email_address" | "password",
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setFieldError(""));
    handleInputChange(id, e.target.value, handleDispatch, handleInputError);
  };
  const handleSubmitOTP = async () => {
    dispatch(setLoading(true)); // Show loading state
    const fullOtp = otp.join(""); // Join OTP array into a string

    if (fullOtp.length === 6) {
      try {
        // Attempt OTP verification
        const cognitoUserSession = await confirmSignIn({
          challengeResponse: fullOtp,
        });
        if (cognitoUserSession.isSignedIn) {
          // If signed in, redirect to the dashboard
          setIsLoggedIn(true);
          const user = await fetchAuthSession({ forceRefresh: true });
          const email = user?.tokens?.idToken?.payload?.email as string || '';
          
          dispatch(setUserEmail(email));
          // identifyOpenReplayUser(email); // OpenReplay disabled for now
          
          // Fetch user details from API
          try {
            const response = await getAPI(`admins/email/${email}`);
            if (!response.error && response.data) {
              dispatch(setUserDetails(response.data));
            }
          } catch (error) {
            console.error('Error fetching user details:', error);
          }
          const privileges = user?.tokens?.idToken?.payload?.['custom:privileges'];
          if (!privileges) {
            dispatch(setPrivilegedArray('superadmin'));

          } else {
            let privilegesArray: string[] = [];
            if (Array.isArray(privileges)) {
              privilegesArray = privileges as string[];
            } else if (typeof privileges === 'string') {
              privilegesArray = privileges.split(','); // Split by comma if privileges are a single string
            }
            let requiredPrivileges = [
              "D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8",
              "D9", "D10", "D11", "D12", "D13", "D14", "D15",
              "D16", "D17", "D18", "D19", "D20", "D21"
            ]
            if (Array.isArray(privilegesArray) && requiredPrivileges.some(privilege => privilegesArray.includes(privilege))) {
              Navigate('/dashboard')
            } else {
              Navigate("/admin-management/list-of-admins");
            }
            dispatch(setPrivilegedArray(privilegesArray));
          }
          setIsLoggedIn(true);
          dispatch(
            showToast({
              type: "success",
              message: translate("Welcome to ZoodPay Admin Panel"),
            })
          );
          setIsSuccess(false);
        } else {
          // Handle unexpected response
          dispatch(setErrorMessage("Invalid OTP"));
        }
      } catch (error: any) {
        console.error("Error verifying OTP:", error.name);

        // Use error.name or error.message for specific error handling
        if (error.name === "InvalidLambdaResponseException") {
          dispatch(setErrorMessage("OTP expired"));
        } else {
          // Fallback for any other errors
          dispatch(setErrorMessage("Invalid OTP"));
        }
      } finally {
        // Always stop loading after the operation
        dispatch(setLoading(false));
      }
    } else {
      // Handle empty or incomplete OTP
      dispatch(setErrorMessage("This field is required"));
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    dispatch(resetLogin());
  }, []);
  return (
    <LoginModel
      instruction={
        !isSuccess
          ? translate("Please enter your credentials to continue")
          : replace(`Enter the code sent to {{email_address}}`, {
            email_address,
          })
      }
      heading={!isSuccess ? translate("Login") : translate("OTP Verification")}
      handleSubmit={!isSuccess ? handleSubmit : handleSubmitOTP}
      loading={loading}
      buttonName={!isSuccess ? undefined : translate("Verify")}
      buttonClassName={undefined}
      handleResendOtp={!isSuccess ? undefined : handleSubmit}
      testId={!isSuccess ? "login-btn" : "verify-btn"}
      mainImg="zood_pay_sidebar_img"
      loginFlow
    >
      {!isSuccess ? (
        <LoginForm
          handleInputValue={handleInputValue}
          errors={errors}
          fieldError={fieldError}
        />
      ) : (
        <Otp />
      )}
    </LoginModel>
  );
}
